import { useQuery, useQueryClient } from '@tanstack/react-query';

import { endpoints, type BulkMapProductsBody, type MapCategoryBody } from './api';
import { usePermittedMutation } from './session';

/**
 * Category-mapping hooks.
 *
 * A product cannot be published to a marketplace until its category is mapped to
 * the channel's own tree. The channel's categories come from a taxonomy sync, so
 * the search returns nothing until that account has synced at least once.
 */

export function useCategoryMappings(channelKey: string) {
    return useQuery({
        queryKey: ['category-mappings', channelKey],
        queryFn: () => endpoints.listCategoryMappings(channelKey),
        enabled: Boolean(channelKey),
    });
}

export function useSearchChannelCategories(channelKey: string, search: string) {
    return useQuery({
        queryKey: ['channel-categories', channelKey, search],
        queryFn: () => endpoints.searchChannelCategories(channelKey, search),
        // Two characters before searching: a one-letter query matches most of the
        // tree and is never what the seller means.
        enabled: Boolean(channelKey) && search.trim().length >= 2,
    });
}

/**
 * The channel categories most like one of ours, ranked, for the mapping drawer
 * to open on before anything is typed (docs/13 §3). Asked by the category's
 * name, so each category's drawer has answers of its own.
 */
export function useChannelCategorySuggestions(channelKey: string, text: string) {
    return useQuery({
        queryKey: ['channel-category-suggestions', channelKey, text],
        queryFn: () => endpoints.suggestChannelCategories(channelKey, text),
        enabled: Boolean(channelKey) && text.trim() !== '',
    });
}

export function useMapCategory(channelKey: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('category.write', {
        mutationFn: ({ categoryId, body }: { categoryId: string; body: MapCategoryBody }) => endpoints.mapCategory(categoryId, body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['category-mappings', channelKey] }),
    });
}

export function useUnmapCategory(channelKey: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('category.write', {
        mutationFn: (categoryId: string) => endpoints.unmapCategory(categoryId, channelKey),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['category-mappings', channelKey] }),
    });
}

/**
 * Per-product mapping hooks.
 *
 * A category mapping settles where a whole category goes; these settle where
 * one product goes when it does not belong with the rest of its category. The
 * product's own answer wins, so anything showing readiness has to be
 * invalidated when it changes.
 */

function invalidateReadiness(queryClient: ReturnType<typeof useQueryClient>, productId: string, channelKey: string) {
    // The attribute view carries which mapping applied and what is still
    // missing, and the product list can be filtered on exactly that, so both
    // are stale the moment a product's category changes.
    queryClient.invalidateQueries({ queryKey: ['product-channel-attributes', productId, channelKey] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
}

export function useMapProductCategory(productId: string, channelKey: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('product.write', {
        mutationFn: (channelCategoryId: string) => endpoints.mapProductCategory(productId, channelKey, channelCategoryId),
        onSuccess: () => invalidateReadiness(queryClient, productId, channelKey),
    });
}

export function useUnmapProductCategory(productId: string, channelKey: string) {
    const queryClient = useQueryClient();
    return usePermittedMutation('product.write', {
        mutationFn: () => endpoints.unmapProductCategory(productId, channelKey),
        onSuccess: () => invalidateReadiness(queryClient, productId, channelKey),
    });
}

/**
 * Pointing many products at one channel category.
 *
 * The same endpoint answers the rehearsal (useBulkMapPreview) and the run, so a
 * seller sees the exact selection they are about to change — including how many
 * of them already carry a choice of their own.
 */
export function useBulkMapProducts() {
    const queryClient = useQueryClient();
    return usePermittedMutation('product.write', {
        mutationFn: (body: BulkMapProductsBody) => endpoints.bulkMapProducts(body),
        onSuccess: (_result, body) => {
            if (body.preview) return;
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['category-mappings', body.channelKey] });
        },
    });
}

/**
 * The rehearsal of a bulk mapping: how many of these products it would touch,
 * and how many already carry a choice of their own — the number that decides
 * whether to overwrite, so it is on screen before the button is pressed. The
 * category is not part of it: it changes where the products go, not which.
 */
export function useBulkMapPreview(channelKey: string, productIds: string[], enabled: boolean) {
    return useQuery({
        queryKey: ['bulk-map-preview', channelKey, productIds],
        queryFn: () =>
            endpoints.bulkMapProducts({
                channelKey,
                // Not used by a rehearsal, but the contract asks for one.
                channelCategoryId: '-',
                productIds: productIds.slice(0, 1000),
                preview: true,
            }),
        enabled: enabled && Boolean(channelKey) && productIds.length > 0,
    });
}

/**
 * Mappings that no longer hold against the channel's current tree.
 *
 * A marketplace reshapes its tree and a mapping made against the old one now
 * points at a category that is closed, has gained children, or is gone. Without
 * this the first sign is a failed publish naming a number.
 */
export function useStaleMappings(channelKey: string) {
    return useQuery({
        queryKey: ['stale-mappings', channelKey],
        queryFn: () => endpoints.staleCategoryMappings(channelKey),
        enabled: Boolean(channelKey),
    });
}

/**
 * What the category mapping page reads from its address: the channel to open
 * on, by key, so a link from a product's channel card lands on that channel
 * rather than on whichever sorts first (EPZ-0110).
 */
export interface MappingPageSearch {
    kanal?: string;
}

/** The route's reading of its address: anything it does not know is dropped. */
export function validateMappingSearch(search: Record<string, unknown>): MappingPageSearch {
    return typeof search.kanal === 'string' && search.kanal !== '' ? { kanal: search.kanal } : {};
}
